import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import FormulairesModal from './FormulairesModal';

function Dashboard() {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState(7);
  const [error, setError] = useState(null);
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    filterType: null,
    title: '',
    subtitle: ''
  });

  // Vérifier si l'utilisateur peut accéder au dashboard
  const canAccess = ['superviseur', 'manager'].includes(user?.role);

  useEffect(() => {
    if (canAccess) {
      fetchDashboardData();
    }
  }, [canAccess, selectedPeriod]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get('/api/dashboard/overview');
      
      if (response.data.success) {
        setDashboardData(response.data.data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement du dashboard:', error);
      setError(error.response?.data?.error || 'Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (!num && num !== 0) return 'N/A';
    return parseInt(num).toLocaleString('fr-FR');
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return 'N/A';
    return `${parseInt(amount).toLocaleString('fr-FR')}€`;
  };

  const formatDuration = (hours) => {
    if (!hours && hours !== 0) return 'N/A';
    const h = Math.floor(hours);
    const m = Math.floor((hours - h) * 60);
    return `${h}h${m.toString().padStart(2, '0')}`;
  };

  const getProgressPercentage = (value, total) => {
    if (!total || total === 0) return 0;
    return Math.round((value / total) * 100);
  };

  // Fonctions pour ouvrir la modale avec différents filtres
  const openModal = (filterType, title, subtitle) => {
    setModalConfig({
      isOpen: true,
      filterType,
      title,
      subtitle
    });
  };

  const closeModal = () => {
    setModalConfig({
      isOpen: false,
      filterType: null,
      title: '',
      subtitle: ''
    });
  };

  const handleTotalFormulairesClick = () => {
    openModal('total', 
      '📊 Tous les Formulaires', 
      `Total de ${formatNumber(dashboardData.kpis.total_formulaires)} formulaires dans le système`
    );
  };

  const handleEnCoursClick = () => {
    // Correspondre exactement à ce qui est compté dans le dashboard
    openModal('en_cours_exact', 
      '⏳ Formulaires En Cours', 
      `${formatNumber(dashboardData.kpis.en_cours)} formulaires avec statut "en cours"`
    );
  };

  const handleTauxResolutionClick = () => {
    // Afficher la répartition complète des statuts de résolution
    openModal('repartition_resolution', 
      '📊 Répartition des Résolutions', 
      `${formatNumber(dashboardData.kpis.valides)} validés • ${formatNumber(dashboardData.kpis.rejetes)} rejetés • Taux: ${getProgressPercentage(dashboardData.kpis.valides, dashboardData.kpis.total_formulaires)}%`
    );
  };

  const handleCoutTotalClick = () => {
    // Pour les coûts, on va créer un graphique interactif
    openModal('cout_chart', 
      '💰 Analyse Graphique des Coûts', 
      `Coût total estimé: ${formatCurrency(dashboardData.kpis.cout_total)} • Répartition par formulaire et équipement`
    );
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'critique': return '#dc3545';
      case 'haute': return '#fd7e14';
      case 'normale': return '#ffc107';
      case 'faible': return '#28a745';
      default: return '#6c757d';
    }
  };

  if (!canAccess) {
    return (
      <div style={{
        padding: '40px',
        textAlign: 'center',
        background: '#f8f9fa',
        borderRadius: '8px',
        margin: '20px'
      }}>
        <h3 style={{ color: '#dc3545' }}>🚫 Accès Restreint</h3>
        <p>Cette section est réservée aux superviseurs et managers.</p>
        <p>Votre rôle actuel : <strong>{user?.role}</strong></p>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ fontSize: '24px', marginBottom: '20px' }}>📊</div>
        <div>Chargement du dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: '20px',
        background: '#f8d7da',
        border: '1px solid #f5c6cb',
        borderRadius: '8px',
        color: '#721c24',
        margin: '20px'
      }}>
        <h4>❌ Erreur de chargement</h4>
        <p>{error}</p>
        <button
          onClick={fetchDashboardData}
          style={{
            background: '#dc3545',
            color: 'white',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          🔄 Réessayer
        </button>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <p>Aucune donnée disponible.</p>
      </div>
    );
  }

  const { kpis, resolution_times, equipment_analytics, time_evolution, top_problems } = dashboardData;

  return (
    <div style={{ padding: '20px', background: '#f8f9fa', minHeight: '100vh' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        
        {/* En-tête */}
        <div style={{
          background: 'white',
          padding: '20px',
          borderRadius: '8px',
          marginBottom: '20px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <h1 style={{ margin: '0 0 10px 0', color: '#9cb85c' }}>
            📊 Dashboard Analytics - 6W2H v2.0
          </h1>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ color: '#6c757d' }}>
              Dernière mise à jour : {new Date(dashboardData.generated_at).toLocaleString('fr-FR')}
            </div>
            <button
              onClick={fetchDashboardData}
              style={{
                background: '#9cb85c',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              🔄 Actualiser
            </button>
          </div>
        </div>

        {/* KPIs Principaux */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '20px',
          marginBottom: '30px'
        }}>
          {/* Total Formulaires */}
          <div 
            onClick={handleTotalFormulairesClick}
            style={{
              background: 'white',
              padding: '20px',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              border: '2px solid transparent'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
              e.currentTarget.style.borderColor = '#9cb85c';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
              e.currentTarget.style.borderColor = 'transparent';
            }}
          >
            <div style={{ fontSize: '32px', color: '#9cb85c', fontWeight: 'bold' }}>
              {formatNumber(kpis.total_formulaires)}
            </div>
            <div style={{ color: '#6c757d', marginTop: '5px' }}>Total Formulaires</div>
            <div style={{ fontSize: '12px', color: '#28a745', marginTop: '10px' }}>
              📈 +{formatNumber(kpis.valides)} résolus
            </div>
            <div style={{ fontSize: '11px', color: '#9cb85c', marginTop: '8px', fontStyle: 'italic' }}>
              👆 Cliquez pour voir le détail
            </div>
          </div>

          {/* En Cours */}
          <div 
            onClick={handleEnCoursClick}
            style={{
              background: 'white',
              padding: '20px',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              border: '2px solid transparent'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
              e.currentTarget.style.borderColor = '#ffc107';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
              e.currentTarget.style.borderColor = 'transparent';
            }}
          >
            <div style={{ fontSize: '32px', color: '#ffc107', fontWeight: 'bold' }}>
              {formatNumber(kpis.en_cours)}
            </div>
            <div style={{ color: '#6c757d', marginTop: '5px' }}>En Cours</div>
            <div style={{ fontSize: '12px', color: '#fd7e14', marginTop: '10px' }}>
              ⏳ {formatNumber(kpis.en_attente)} en attente
            </div>
            <div style={{ fontSize: '11px', color: '#ffc107', marginTop: '8px', fontStyle: 'italic' }}>
              👆 Cliquez pour voir le détail
            </div>
          </div>

          {/* Taux de Résolution */}
          <div 
            onClick={handleTauxResolutionClick}
            style={{
              background: 'white',
              padding: '20px',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              border: '2px solid transparent'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
              e.currentTarget.style.borderColor = '#28a745';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
              e.currentTarget.style.borderColor = 'transparent';
            }}
          >
            <div style={{ fontSize: '32px', color: '#28a745', fontWeight: 'bold' }}>
              {getProgressPercentage(kpis.valides, kpis.total_formulaires)}%
            </div>
            <div style={{ color: '#6c757d', marginTop: '5px' }}>Taux de Résolution</div>
            <div style={{ fontSize: '12px', color: '#dc3545', marginTop: '10px' }}>
              ❌ {formatNumber(kpis.rejetes)} rejetés
            </div>
            <div style={{ fontSize: '11px', color: '#28a745', marginTop: '8px', fontStyle: 'italic' }}>
              👆 Cliquez pour voir le détail
            </div>
          </div>

          {/* Coût Total */}
          <div 
            onClick={handleCoutTotalClick}
            style={{
              background: 'white',
              padding: '20px',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              border: '2px solid transparent'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
              e.currentTarget.style.borderColor = '#dc3545';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
              e.currentTarget.style.borderColor = 'transparent';
            }}
          >
            <div style={{ fontSize: '32px', color: '#dc3545', fontWeight: 'bold' }}>
              {formatCurrency(kpis.cout_total)}
            </div>
            <div style={{ color: '#6c757d', marginTop: '5px' }}>Coût Total Estimé</div>
            <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '10px' }}>
              💰 Moy: {formatCurrency(kpis.cout_moyen)}
            </div>
            <div style={{ fontSize: '11px', color: '#dc3545', marginTop: '8px', fontStyle: 'italic' }}>
              👆 Cliquez pour voir le détail
            </div>
          </div>
        </div>

        {/* Temps de Résolution */}
        {resolution_times && (
          <div style={{
            background: 'white',
            padding: '20px',
            borderRadius: '8px',
            marginBottom: '20px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ margin: '0 0 15px 0', color: '#9cb85c' }}>⏱️ Performance de Résolution</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
              <div>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#007bff' }}>
                  {formatDuration(resolution_times.temps_moyen_heures)}
                </div>
                <div style={{ color: '#6c757d' }}>Temps Moyen de Résolution</div>
              </div>
              <div>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#28a745' }}>
                  {formatNumber(resolution_times.formulaires_resolus)}
                </div>
                <div style={{ color: '#6c757d' }}>Formulaires Résolus</div>
              </div>
              <div>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#dc3545' }}>
                  {formatNumber(resolution_times.en_retard)}
                </div>
                <div style={{ color: '#6c757d' }}>En Retard (>48h)</div>
              </div>
            </div>
          </div>
        )}

        {/* Top Équipements Problématiques */}
        {equipment_analytics && equipment_analytics.length > 0 && (
          <div style={{
            background: 'white',
            padding: '20px',
            borderRadius: '8px',
            marginBottom: '20px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ margin: '0 0 15px 0', color: '#9cb85c' }}>🔧 Top Équipements Problématiques</h3>
            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {equipment_analytics.slice(0, 5).map((equipment, index) => (
                <div
                  key={equipment.equipement_zone_ligne}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px',
                    marginBottom: '8px',
                    background: index === 0 ? '#fff3cd' : '#f8f9fa',
                    borderLeft: `4px solid ${index === 0 ? '#ffc107' : '#dee2e6'}`,
                    borderRadius: '4px'
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 'bold' }}>
                      #{index + 1} {equipment.equipement_zone_ligne}
                    </div>
                    <div style={{ fontSize: '12px', color: '#6c757d' }}>
                      {formatNumber(equipment.resolus)}/{formatNumber(equipment.total_problemes)} résolus
                      • {formatCurrency(equipment.cout_total)}
                    </div>
                  </div>
                  <div style={{
                    background: index === 0 ? '#ffc107' : '#28a745',
                    color: 'white',
                    padding: '4px 12px',
                    borderRadius: '15px',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}>
                    {formatNumber(equipment.total_problemes)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Problèmes Récurrents */}
        {top_problems && top_problems.length > 0 && (
          <div style={{
            background: 'white',
            padding: '20px',
            borderRadius: '8px',
            marginBottom: '20px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ margin: '0 0 15px 0', color: '#9cb85c' }}>🔁 Problèmes Récurrents</h3>
            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {top_problems.slice(0, 5).map((problem, index) => (
                <div
                  key={index}
                  style={{
                    padding: '12px',
                    marginBottom: '8px',
                    background: '#f8f9fa',
                    borderLeft: '4px solid #dc3545',
                    borderRadius: '4px'
                  }}
                >
                  <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>
                    {problem.probleme_normalise.length > 60 
                      ? problem.probleme_normalise.substring(0, 60) + '...'
                      : problem.probleme_normalise
                    }
                  </div>
                  <div style={{ fontSize: '12px', color: '#6c757d' }}>
                    <span style={{ color: '#dc3545', fontWeight: 'bold' }}>
                      {formatNumber(problem.occurrences)} occurrences
                    </span>
                    • {formatNumber(problem.resolus)} résolus
                    • Coût moyen: {formatCurrency(problem.cout_moyen)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Évolution Temporelle */}
        {time_evolution && time_evolution.length > 0 && (
          <div style={{
            background: 'white',
            padding: '20px',
            borderRadius: '8px',
            marginBottom: '20px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ margin: '0 0 15px 0', color: '#9cb85c' }}>📈 Évolution (7 derniers jours)</h3>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: `repeat(${time_evolution.length}, 1fr)`, 
              gap: '10px',
              marginTop: '15px'
            }}>
              {time_evolution.map((day, index) => (
                <div
                  key={day.jour}
                  style={{
                    textAlign: 'center',
                    padding: '10px',
                    background: '#f8f9fa',
                    borderRadius: '4px'
                  }}
                >
                  <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '5px' }}>
                    {new Date(day.jour).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric' })}
                  </div>
                  <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#9cb85c' }}>
                    {formatNumber(day.nouveaux_formulaires)}
                  </div>
                  <div style={{ fontSize: '12px', color: '#28a745' }}>
                    ✅ {formatNumber(day.resolus)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Informations Système */}
        <div style={{
          background: 'white',
          padding: '20px',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          fontSize: '14px',
          color: '#6c757d'
        }}>
          <h4 style={{ margin: '0 0 10px 0', color: '#9cb85c' }}>ℹ️ Informations Système</h4>
          <p>
            <strong>Votre rôle :</strong> {user?.role} - 
            <strong> Accès :</strong> {user?.role === 'manager' ? 'Complet (toutes métriques)' : 'Superviseur (métriques équipe)'}
          </p>
          <p>
            <strong>Données Ligne B3 :</strong> Coût d'arrêt estimé à 10 000€/heure (166,67€/minute)
          </p>
          <p>
            <strong>Dernière actualisation :</strong> {new Date(dashboardData.generated_at).toLocaleString('fr-FR')}
          </p>
        </div>
      </div>

      {/* Modale interactive des formulaires */}
      <FormulairesModal
        isOpen={modalConfig.isOpen}
        onClose={closeModal}
        filterType={modalConfig.filterType}
        title={modalConfig.title}
        subtitle={modalConfig.subtitle}
      />
    </div>
  );
}

export default Dashboard;
